import events from "add-event-listener";
import * as React from "react";
import { KonturColors } from "@skbkontur/colors";
import { DropdownMenu, Hint, MenuItem, MenuSeparator, RenderLayer, Spinner, Toast } from "react-ui";
import { ReloadFunction } from "@kontur.candy/candy-widget-npm-loader";

import { HideUnitButtonContainer as HideUnitButton } from "../HideUnitButton/HideUnitButtonContainer";
import { FormSettings } from "../../../../Forms/Types/FormSettings";
import {
    CLEAR_PANEL_CLICK,
    CONVERT_PANEL_CLICK,
    ENABLE_AUTO_VALUES,
    FEEDBACK_PANEL_CLICK,
    FUF_PANEL_CLICK,
    HIDE_ORG_NAME_PANEL_CLICK,
    HISTORY_EDITING_OPEN_CLICK,
    MORE_ACTIONS_CLICK,
    PRINT_PANEL_CLICK,
    RESTORE_TO_SOURCE_FUF_CLICK,
    TEMPLATES_PANEL_CLICK,
    TOUR_PANEL_CLICK,
    UPLOAD_FUF_ATTACHMENTS_PANEL_CLICK,
    UPLOAD_PANEL_CLICK,
} from "../../../metricsAction";
import { emptyRef } from "../../../TypingUtils";
import { importTourStep as importStep } from "../../Tour/Steps/Import";
import { showTourButtonTourStep as stepShowTour } from "../../Tour/Steps/ShowTourButtonStep";
import { FormSaveStatusContainer as FormSaveStatus } from "../FormSaveStatus/FormSaveStatusContainer";
import { PanelType } from "../PanelTypes";
import { TemplatesModalContainer } from "../TemplatesModal/TemplatesModalContainer";
import { FufUploadModalContainer } from "../FufUploadModal/FufUploadModalContainer";
import { ViewModeSelectButtonContainer } from "../../../Internals/ViewModeSelector/ViewModeSelectButton/ViewModeSelectButtonContainer";
import { feedbackButtonTourStep } from "../../Tour/Steps/FeedbackButton";
import { uiSettingsTourStep } from "../../Tour/Steps/UISettings";
import { TourTargetElement } from "../../../Internals/TourManagement/TourTargetElement";
import { Icon } from "../../../Icons/Icon";
import { ActionDescription } from "../../../../Forms/Client/Client";
import { RecalculateButtonContainer } from "../../ActionButton/RecalculateButtonContainer";
import { HistoryEditing } from "../../HistoryEditing/HistoryEditing";
import { TooltipStepContainer } from "../../../Layout/TooltipStep/TooltipStepContainer";
import { TourStepsContextObject } from "../../../Internals/TourManagement/TourStepsContextObject";
import { useMetrics } from "../../../Internals/Metrics/MetricsProviderContext";
import {
    UserFriendlyLoadingState,
    useUserFriendlyLoadingState,
} from "../../UserPicklistDeclaration/SelectNidusEntryModal/SelectEntryModal/UserFriendlyLoadingState";
import { FufAndAttachmentsUploadModalContainer } from "../FufAndAttachmentsUploadModal/FufAndAttachmentsUploadModalContainer";
import { AttachmentNames } from "../FufAndAttachmentsUploadModal/FufAndAttachmentsUploadModalTypes";

import { ActionButtonsWrapper, ActionsBar, ActionsBarButton, Root } from "./ActionPanel.styles";
import { customizeActionsSort } from "./Helpers";
import { ConvertDraftModal } from "./ConvertDraftModal/ConvertDraftModal";
import { ActionPanelFormActions } from "./ActionPanelFormActions/ActionPanelFormActions";

const actions: ActionType[] = [
    "print",
    "fuf",
    "templates",
    "fufUpload",
    "customization",
    "feedback",
    "import",
    "tour",
    "orgName",
    "inner",
    "enableAutoValues",
    "remove",
    "historyEditing",
    "fufAndAttachmentsUpload",
    "restoreToSourceFuf",
];

export type ActionType =
    | "print"
    | "feedback"
    | "import"
    | "tour"
    | "orgName"
    | "templates"
    | "fuf"
    | "inner"
    | "enableAutoValues"
    | "remove"
    | "fufUpload"
    | "customization"
    | "historyEditing"
    | "restoreToSourceFuf"
    | "fufAndAttachmentsUpload";

interface ActionDescriptionLocal extends ActionDescription {
    condition: (formSettings: FormSettings, props: ActionPanelProps) => boolean | undefined | string;
    disabled?: () => boolean;
    loading?: () => boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getProps?: () => any;
}

export interface ActionPanelProps {
    className?: string;
    checkErrors: () => void;
    hiddenConvertLink: boolean;
    isHideOrgName?: boolean;
    isPopupPanel: boolean;
    panelState: PanelType;
    setHiddenOrgName: (x: boolean) => void;
    setPanelState: (panelType: PanelType) => void;
    setPopupPanel: (x: boolean) => void;
    transferText?: string;
    uploadFufButtonEnabled: boolean;
    uploadFufAttachmentsButtonEnabled: boolean;
    fufSizeTooLargeErrorText?: string | React.ReactNode;
    uploadFufLightboxHideDemandNameButton: boolean;
    uploadFufLightboxTitle?: string;
    uploadFufLightboxAlert?: string;
    onSaveForm: () => Promise<void>;
    onSaveFuf: () => Promise<void>;
    onFormClear: () => Promise<void>;
    onRestoreToSourceFuf: () => Promise<void>;
    onRunFeedback: () => void;
    formSettings: FormSettings;
    onSaveInner: () => Promise<void>;
    onPrintFuf: () => Promise<void>;
    onEnableAutoValues: () => Promise<void>;
    onShowTour: () => void;
    onConvertDraftFromPrevGfv: () => Promise<void>;
    onRunExternalInterdocumentTransfer: () => void;
    isFufDownloading: boolean;
    isInnerDownloading: boolean;
    reload: ReloadFunction;
    showDraftHistoryInEditor: boolean;
    attachmentNames?: AttachmentNames;
}

type ActionsDescriptionType = { [action in ActionType]: ActionDescriptionLocal };

const READONLY_ACTION_LIST_ALLOWED = ["fuf"];

export const ActionPanel = (props: ActionPanelProps) => {
    const track = useMetrics();
    const tourStepContext = React.useContext(TourStepsContextObject);

    const uiSettingsButton = React.useRef<HTMLButtonElement>(emptyRef);
    const anchor = React.useRef<HTMLDivElement & HTMLSpanElement & HTMLLIElement & HTMLButtonElement>(emptyRef);

    const [showConvertModal, setShowConvertModal] = React.useState(false);
    const [showHistoryEditingWidget, setShowHistoryEditingWidget] = React.useState(false);
    const [historyWidgetIsLoading, setHistoryWidgetIsLoading] = React.useState(false);
    const [showTemplatesModal, setShowTemplatesModal] = React.useState(false);
    const [showFufUploadModal, setShowFufUploadModal] = React.useState(false);
    const [isTourOpen, setIsTourOpen] = React.useState(true);
    const [tourState, setTourState] = React.useState(1);
    const [showFufAttachmentsUploadModal, setShowFufAttachmentsUploadModal] = React.useState(false);

    const historyWidgetLoadingState = useUserFriendlyLoadingState(historyWidgetIsLoading);

    const handleScrollListener = React.useCallback(() => {
        if (props.isPopupPanel) {
            props.setPopupPanel(false);
        }
    }, [props.isPopupPanel, props.setPopupPanel]);
    const handleResizeListener = React.useCallback(() => {
        if (props.isPopupPanel) {
            props.setPopupPanel(false);
        }
    }, [props.isPopupPanel, props.setPopupPanel]);

    React.useEffect(() => {
        setTourSteps();

        events.addEventListener(window, "scroll", handleScrollListener);
        events.addEventListener(window, "resize", handleResizeListener);
        return () => {
            events.removeEventListener(window, "scroll", handleScrollListener);
            events.removeEventListener(window, "resize", handleResizeListener);
        };
    }, []);

    const actionsDescription: ActionsDescriptionType = {
        print: {
            condition: (formSettings: FormSettings) => formSettings.printFromEditor || formSettings.isFormInFormNds,
            icon: "TechPrinterIcon",
            text: "Напечатать",
            onClick: () => sendPrintEvent(),
            hint: props.formSettings.printInfo.message,
            disabled: () => !props.formSettings.printInfo.available,
            tid: "ActionPrint",
            id: "print",
            weight: 10,
        },
        fuf: {
            condition: (formSettings: FormSettings) => !formSettings.hideFufDownloadButton,
            icon: "ArrowFigureASquareDownIcon",
            text: "Скачать файл отчета",
            loading: () => props.isFufDownloading,
            onClick: () => handleOnClickFufSave(),
            tid: "ActionDownload",
            id: "fuf",
            weight: 20,
        },
        historyEditing: {
            condition: (formSettings: FormSettings) =>
                formSettings.innerHistoryEnabled && props.showDraftHistoryInEditor,
            onClick: () => handleOnOpenHistoryWidget(),
            disabled: () => historyWidgetLoadingState >= UserFriendlyLoadingState.LoadingState1,
            loading: () => historyWidgetLoadingState >= UserFriendlyLoadingState.LoadingState2,
            icon: "TimeClockMoveBackIcon",
            text: "История изменений",
            tid: "panel-action-history-editing",
            id: "historyEditing",
            weight: 30,
        },
        fufAndAttachmentsUpload: {
            condition: (formSettings: FormSettings) =>
                props.uploadFufAttachmentsButtonEnabled && formSettings.isFormInFormNds,
            icon: "NetUploadIcon",
            text: "Загрузить файл отчета с приложениями",
            onClick: () => handleOnClickFufAndAttachmentsUpload(),
            tid: "panel-action-fuf-and-attachments-upload",
            id: "fuf-and-attachments-upload",
            weight: 40,
        },
        import: {
            condition: (formSettings: FormSettings, panelProps: ActionPanelProps) =>
                (formSettings.prevGfvDraftId || formSettings.interdocumentTransfer) && !panelProps.hiddenConvertLink,
            icon: "DocArrowConvertIcon",
            text: "Перенести данные",
            onClick: () => handleOnClickConvertDraft(),
            tid: "panel-action-import",
            id: "import",
            weight: 50,
        },
        feedback: {
            icon: "StarIcon",
            text: "Оставить отзыв",
            condition: (formSettings: FormSettings) => formSettings.feedbackEnabled,
            onClick: () => sendShowFeedbackEvent(),
            tid: "ActionFeedback",
            id: "feedback",
            weight: 60,
        },
        fufUpload: {
            condition: (formSettings: FormSettings) =>
                props.uploadFufButtonEnabled ||
                (formSettings.isFormInFormNds && !props.uploadFufAttachmentsButtonEnabled),
            icon: "NetUploadIcon",
            text: "Загрузить файл отчета",
            onClick: () => handleOnClickFufUpload(),
            tid: "panel-action-fuf-upload",
            id: "fuf-upload",
            weight: 70,
        },
        templates: {
            condition: (formSettings: FormSettings) =>
                formSettings.hasTemplates && formSettings.templatesApi != undefined,
            onClick: () => handleOnOpenModal(),
            icon: "DocsIcon",
            text: "Шаблоны",
            tid: "panel-action-templates",
            id: "templates",
            weight: 80,
        },
        tour: {
            condition: (formSettings: FormSettings) => !formSettings.isDisabledTour || formSettings.isFormInFormNds,
            text: "Показать подсказки",
            icon: "LightbulbIcon",
            onClick: () => {
                sendShowTourEvent();
                setTimeout(() => props.setPopupPanel(false), 0);
            },
            tid: "panel-action-tour",
            id: "tour",
            weight: 90,
        },
        orgName: {
            condition: (formSettings: FormSettings) => formSettings.isINNEnabled,
            text: () => (props.isHideOrgName ? "Показать названия организаций" : "Скрыть названия организаций"),
            icon: "BuildingOfficeIcon",
            onClick: () => hideOrgNameClick(),
            getProps: () => ({ isHideOrgName: props.isHideOrgName }),
            tid: "panel-action-orgname",
            id: "orgname",
            weight: 100,
        },
        enableAutoValues: {
            //only for rsv gfv = 117201..117207
            condition: (formSettings: FormSettings) => formSettings.gfv.startsWith("1172"),
            icon: "MathFunctionIcon",
            text: "Включить авторасчет",
            onClick: () => handleOnClickEnableAutoValue(),
            tid: "panel-action-enable-auto",
            id: "fillAuto",
            weight: 110,
        },
        inner: {
            condition: (formSettings: FormSettings) => formSettings.isTestServer,
            icon: "InfoSquareIcon",
            text: "Сохранить inner",
            loading: () => props.isInnerDownloading,
            onClick: () => handleOnClickInnerSave(),
            getProps: () => ({ download: true }),
            tid: "panel-action-inner",
            id: "inner",
            weight: 110,
        },
        restoreToSourceFuf: {
            condition: (formSettings: FormSettings) => formSettings.restoreToSourceFuf,
            icon: "DocArrowInIcon",
            text: "Вернуть исходные данные",
            onClick: () => handleOnClickRestoreToSourceFuf(),
            tid: "restore-to-source-fuf",
            id: "restoreToSourceFuf",
            weight: 129,
        },
        remove: {
            condition: (formSettings: FormSettings) => !formSettings.isDisabledCleanForm,
            icon: "XIcon",
            text: "Очистить форму",
            onClick: () => handleOnClickClear(),
            tid: "panel-action-remove",
            id: "remove",
            weight: 130,
        },
        customization: {
            condition: (formSettings: FormSettings) => formSettings.enableCustomizationButton,
            icon: "SettingsSlidersH3Icon",
            text: "Настроить оформление",
            // eslint-disable-next-line no-empty-function
            onClick: () => {},
            tid: "panel-action-customization",
            id: "customization",
            weight: 140,
        },
    };

    const setTourSteps = (): void => {
        if (tourStepContext != undefined) {
            const displayedActions = getDisplayedActions();

            const tourGroup =
                displayedActions.length < 4 || displayedActions.indexOf("tour") < 2 ? ["panel"] : ["panel", "menu"];

            if (getAllActionDescriptions().tour.condition(props.formSettings, props)) {
                tourStepContext.addTourStep({ ...stepShowTour, group: tourGroup });
            }

            if (displayedActions.includes("import")) {
                tourStepContext.addTourStep(importStep);
            }

            if (displayedActions.includes("feedback")) {
                tourStepContext.addTourStep({ ...feedbackButtonTourStep, controlId: "feedback_tour_button" });
            }

            tourStepContext.addTourStep(uiSettingsTourStep, uiSettingsButton.current ?? undefined);
        }
    };

    const getAllActionDescriptions = (): ActionsDescriptionType => {
        const allActions = { ...actionsDescription };
        for (const externalAction of props.formSettings.panelActions ?? []) {
            // @ts-ignore
            allActions[externalAction.id] = {
                ...externalAction,
                condition: () => true,
            };
        }

        return allActions;
    };

    const getDisplayedActions = (): ActionType[] => {
        const allActionsDescription = getAllActionDescriptions();
        const compareFunction = (a: ActionType, b: ActionType) =>
            allActionsDescription[a].weight - allActionsDescription[b].weight;

        const allActions: ActionType[] = actions.concat(
            (props.formSettings.panelActions ?? []).map(externalAction => externalAction.id as ActionType)
        );

        if (props.formSettings.isReadOnly === true) {
            const readonlyAllowedActions = READONLY_ACTION_LIST_ALLOWED.concat(
                (props.formSettings.panelActions ?? []).map(x => x.id)
            );
            return allActions
                .filter(action => readonlyAllowedActions.includes(allActionsDescription[action].id))
                .filter(action => allActionsDescription[action].condition(props.formSettings, props))
                .sort(compareFunction);
        }

        return allActions
            .filter(action => allActionsDescription[action].condition(props.formSettings, props))
            .sort(compareFunction);
    };

    // region Handlers
    const sendPrintEvent = async (): Promise<void> => {
        track(PRINT_PANEL_CLICK);
        await props.onPrintFuf();
    };

    const handleOnClickFufSave = async (): Promise<void> => {
        track(FUF_PANEL_CLICK);
        if (props.isFufDownloading) {
            return;
        }
        Toast.push("Началось скачивание файла");
        await props.onSaveFuf();
    };

    const handleOnOpenHistoryWidget = async (): Promise<void> => {
        track(HISTORY_EDITING_OPEN_CLICK);
        setShowHistoryEditingWidget(true);
    };

    const handleOnClickConvertDraft = (): void => {
        track(CONVERT_PANEL_CLICK);
        if (props.formSettings.interdocumentTransfer) {
            props.onRunExternalInterdocumentTransfer();
        } else {
            setShowConvertModal(true);
        }
    };

    const sendShowFeedbackEvent = (): void => {
        track(FEEDBACK_PANEL_CLICK);
        props.onRunFeedback();
    };

    const handleOnClickFufUpload = async (): Promise<void> => {
        track(UPLOAD_PANEL_CLICK);
        await props.onSaveForm();
        setShowFufUploadModal(true);
    };

    const handleOnClickFufAndAttachmentsUpload = async (): Promise<void> => {
        track(UPLOAD_FUF_ATTACHMENTS_PANEL_CLICK);
        await props.onSaveForm();
        setShowFufAttachmentsUploadModal(true);
    };

    const handleOnOpenModal = async (): Promise<void> => {
        track(TEMPLATES_PANEL_CLICK);
        await props.onSaveForm();
        setShowTemplatesModal(true);
    };

    const sendShowTourEvent = (): void => {
        track(TOUR_PANEL_CLICK);
        props.onShowTour();
    };

    const hideOrgNameClick = (): void => {
        track(HIDE_ORG_NAME_PANEL_CLICK, { pinState: props.isHideOrgName ? "showOrgName" : "hideOrgName" });
        props.setHiddenOrgName(!props.isHideOrgName);
    };

    const handleOnClickEnableAutoValue = async (): Promise<void> => {
        track(ENABLE_AUTO_VALUES);
        await props.onEnableAutoValues();
    };

    const handleOnClickInnerSave = async (): Promise<void> => {
        if (props.isInnerDownloading) {
            return;
        }
        Toast.push("Началось скачивание файла");
        await props.onSaveInner();
    };

    const handleOnClickClear = async (): Promise<void> => {
        track(CLEAR_PANEL_CLICK);
        await props.onFormClear();
    };

    const handleOnClickRestoreToSourceFuf = async (): Promise<void> => {
        track(RESTORE_TO_SOURCE_FUF_CLICK);
        await props.onRestoreToSourceFuf();
    };

    const handleOnClickMoreActions = () => {
        track(MORE_ACTIONS_CLICK);
        if (props.panelState === "hideManual" || props.panelState === "hide") {
            props.setPanelState("show");
        }
        props.setPopupPanel(true);
    };

    const handleOnClickOutsidePopup = () => {
        if (!(showConvertModal || showTemplatesModal)) {
            props.setPopupPanel(false);
        }
    };
    // endregion

    // region Components
    const renderConvertDraftModal = (): JSX.Element => (
        <ConvertDraftModal
            onClose={(): void => setShowConvertModal(false)}
            onConvertDraftFromPrevGfv={props.onConvertDraftFromPrevGfv}
            transferText={props.transferText}
        />
    );

    const renderHistoryEditingWidget = (): JSX.Element => (
        <HistoryEditing
            keformsUrl={props.formSettings.keformsUrl}
            ns={props.formSettings.ns}
            draftId={props.formSettings.draftId}
            onClose={() => setShowHistoryEditingWidget(false)}
            onBeginLoad={() => setHistoryWidgetIsLoading(true)}
            onCompleteLoad={() => setHistoryWidgetIsLoading(false)}
            reload={props.reload}
            isProdEnvironment={window.location.hostname.endsWith(".kontur.ru")}
        />
    );

    const renderVisibleAction = (action: ActionType): JSX.Element => {
        const actionInfo = getAllActionDescriptions()[action];

        const defaultAction = (
            <Hint
                key={actionInfo.id}
                text={
                    actionInfo.loading?.()
                        ? "Идет загрузка"
                        : actionInfo.hint ||
                          (typeof actionInfo.text === "function" ? actionInfo.text() : actionInfo.text)
                }
                useWrapper={false}>
                <ActionsBarButton
                    onClick={actionInfo.onClick}
                    disabled={actionInfo.disabled?.()}
                    loading={actionInfo.loading?.()}
                    data-tid={actionInfo.tid}>
                    {actionInfo.loading?.() ? (
                        <Spinner type="mini" color={KonturColors.primaryDefault} caption="" />
                    ) : (
                        <Icon name={actionInfo.icon} size={24} />
                    )}
                </ActionsBarButton>
            </Hint>
        );

        if (action === "feedback") {
            return (
                <TourTargetElement key={actionInfo.id} controlId={"feedback_tour_button"}>
                    {defaultAction}
                </TourTargetElement>
            );
        }

        if (action === "customization") {
            return (
                <ViewModeSelectButtonContainer key={actionInfo.id}>
                    {(onClick, opened, menu) => (
                        <Hint text={opened ? null : actionInfo.text} useWrapper={false}>
                            <ActionsBarButton
                                ref={uiSettingsButton}
                                active={opened}
                                disabled={actionInfo.disabled?.()}
                                onClick={onClick}
                                data-tid={actionInfo.tid}>
                                <Icon name={actionInfo.icon} size={24} />
                                {menu}
                            </ActionsBarButton>
                        </Hint>
                    )}
                </ViewModeSelectButtonContainer>
            );
        }

        if (action === "tour") {
            return (
                <TourTargetElement key={actionInfo.id} controlId={"show-tour-button"}>
                    {defaultAction}
                </TourTargetElement>
            );
        }

        if (action === "historyEditing" && tourState === 1 && isTourOpen && props.showDraftHistoryInEditor) {
            return (
                <TooltipStepContainer
                    key={actionInfo.id}
                    id={"historyEditingActionPanel"}
                    isOpen={isTourOpen}
                    openedFrom={"ActionList"}
                    label={"Теперь вам доступна история изменений черновика"}
                    positions={["top left"]}
                    width={270}
                    onNext={() => setTourState(tourState + 1)}
                    onPrev={() => setTourState(tourState - 1)}
                    onClose={() => setIsTourOpen(false)}
                    stepIndex={0}
                    stepsCount={2}>
                    {defaultAction}
                </TooltipStepContainer>
            );
        }

        return defaultAction;
    };

    const renderPopupAction = (action: ActionType): Array<JSX.Element | null> => {
        const actionInfo = getAllActionDescriptions()[action];

        return [
            action === "remove" || action === "restoreToSourceFuf" ? (
                <MenuSeparator key={`pre-${actionInfo.id}`} />
            ) : null,
            <MenuItem
                key={actionInfo.id}
                data-tid={`panel-action-${actionInfo.id}`}
                onClick={actionInfo.onClick}
                disabled={actionInfo.disabled?.() || actionInfo.loading?.()}>
                {actionInfo.loading?.() && <Spinner type="mini" dimmed caption="" />}{" "}
                {typeof actionInfo.text === "function" ? actionInfo.text() : actionInfo.text}
            </MenuItem>,
            action === "templates" ? <MenuSeparator key={`post-${actionInfo.id}`} /> : null,
        ];
    };

    const renderCompactActionList = (): JSX.Element => {
        let allActions = getDisplayedActions();
        const VISIBLE_ACTIONS_LIMIT = allActions.length > 5 ? 4 : 5;
        allActions = customizeActionsSort(allActions, VISIBLE_ACTIONS_LIMIT);
        const visibleActions = allActions.slice(0, VISIBLE_ACTIONS_LIMIT);
        const popupActions = allActions.slice(VISIBLE_ACTIONS_LIMIT, allActions.length);

        const renderVisibleActions = visibleActions.map(action => renderVisibleAction(action));
        const renderPopupActions = popupActions.map(action => renderPopupAction(action)).flat();

        const getCaption = () => (
            <Hint text={props.isPopupPanel ? null : "Ещё действия"} useWrapper={false}>
                <ActionsBarButton ref={anchor} onClick={handleOnClickMoreActions} data-tid="MoreActionsList">
                    <Icon name="UiMenuDots3VIcon" size={24} />
                </ActionsBarButton>
            </Hint>
        );

        return (
            <RenderLayer
                active={props.isPopupPanel}
                onClickOutside={handleOnClickOutsidePopup}
                onFocusOutside={handleOnClickOutsidePopup}>
                <ActionsBar data-tid="ActionList">
                    {renderVisibleActions}
                    {popupActions.length > 0 && (
                        <TourTargetElement controlId={"show-tour-button"}>
                            {props.formSettings.innerHistoryEnabled &&
                            props.showDraftHistoryInEditor &&
                            tourState === 2 &&
                            isTourOpen ? (
                                <TooltipStepContainer
                                    isOpen={isTourOpen}
                                    minitourEnabled={true}
                                    id={"historyEditingActionPanel"}
                                    openedFrom={"ActionList"}
                                    label={"Шаблоны можно найти в этом меню"}
                                    positions={["top left"]}
                                    width={280}
                                    onNext={() => setTourState(tourState + 1)}
                                    onPrev={() => setTourState(tourState - 1)}
                                    onClose={() => setIsTourOpen(false)}
                                    stepIndex={1}
                                    stepsCount={2}
                                    customFooterButtonText={"Понятно"}>
                                    <DropdownMenu positions={["top left"]} caption={getCaption()}>
                                        {renderPopupActions}
                                    </DropdownMenu>
                                </TooltipStepContainer>
                            ) : (
                                <DropdownMenu positions={["top left"]} caption={getCaption()}>
                                    {renderPopupActions}
                                </DropdownMenu>
                            )}
                        </TourTargetElement>
                    )}
                </ActionsBar>
            </RenderLayer>
        );
    };
    // endregion

    return (
        <Root className={props.className}>
            <HideUnitButton />
            {showConvertModal && renderConvertDraftModal()}
            {showHistoryEditingWidget && renderHistoryEditingWidget()}
            {showTemplatesModal && (
                <TemplatesModalContainer
                    onClose={() => {
                        setShowTemplatesModal(false);
                    }}
                    onSaveForm={props.onSaveForm}
                />
            )}
            {showFufUploadModal && (
                <FufUploadModalContainer
                    fufSizeTooLargeErrorText={props.fufSizeTooLargeErrorText}
                    uploadFufLightboxHideDemandNameButton={props.uploadFufLightboxHideDemandNameButton}
                    uploadFufLightboxTitle={props.uploadFufLightboxTitle}
                    uploadFufLightboxAlert={props.uploadFufLightboxAlert}
                    onClose={() => {
                        setShowFufUploadModal(false);
                    }}
                />
            )}
            {showFufAttachmentsUploadModal && (
                <FufAndAttachmentsUploadModalContainer
                    attachmentNames={props.attachmentNames ?? {}}
                    onClose={() => setShowFufAttachmentsUploadModal(false)}
                />
            )}
            {renderCompactActionList()}
            <ActionButtonsWrapper>
                <ActionPanelFormActions formSettings={props.formSettings} checkErrors={props.checkErrors} />
            </ActionButtonsWrapper>
            <RecalculateButtonContainer />
            {!props.formSettings.isReadOnly && <FormSaveStatus />}
        </Root>
    );
};
